import { useEffect, useMemo } from 'react';
import { Box, Drawer, IconButton, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RxCross2 } from 'react-icons/rx';
import { useForm } from 'react-hook-form';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import { BusinessProfileResponse } from 'apis/business/types';
import { useCreateTaggings, useDestroyTagging, useGetTaggingContexts } from '@/apis/agency/taggings/hook';
import { buildSelectOptionsFromContexts, parseTagValue, tagValue } from './businessTags';

interface FormValues {
  tags: string[];
}

interface Props {
  open: boolean;
  business: BusinessProfileResponse | null;
  onClose: () => void;
}

export default function AddBusinessTagsDrawer({ open, business, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { tags: [] }
  });

  const { data: contexts, isLoading: contextsLoading } = useGetTaggingContexts();
  const { mutateAsync: createTags, isPending: isCreating } = useCreateTaggings();
  const { mutateAsync: destroyTag, isPending: isDestroying } = useDestroyTagging();

  const isPending = isCreating || isDestroying;

  useEffect(() => {
    if (open && business) {
      reset({
        tags: (business.tags ?? []).map((tag) => tagValue(tag.context, tag.name))
      });
    }
    if (!open) {
      reset({ tags: [] });
    }
  }, [open, business, reset]);

  const tagOptions = useMemo(() => buildSelectOptionsFromContexts(contexts), [contexts]);

  const saveTags = async (values: FormValues) => {
    if (!business) return;

    const existingTags = business.tags ?? [];
    const existingValues = new Set(existingTags.map((tag) => tagValue(tag.context, tag.name)));
    const selectedValues = new Set(values.tags);

    const tagsToAdd = values.tags.flatMap((value) => {
      if (existingValues.has(value)) return [];
      const parsed = parseTagValue(value);
      return parsed ? [parsed] : [];
    });
    const tagsToRemove = existingTags.filter((tag) => !selectedValues.has(tagValue(tag.context, tag.name)));

    await Promise.all(tagsToRemove.map((tag) => destroyTag(tag.id)));

    if (tagsToAdd.length > 0) {
      await createTags({
        tagging: {
          taggable_type: 'Business',
          taggable_id: Number(business.id),
          tags: tagsToAdd
        }
      });
    }

    onClose();
  };

  const onSubmit = (values: FormValues) => {
    void saveTags(values);
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 480,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={2.5}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography fontSize={20} fontWeight={500}>
          Add Tags to Business
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <RxCross2 />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={2.5} py={2.5}>
        <Box
          component='form'
          id='add-business-tags-form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          flexDirection='column'
          gap={2.5}
        >
          <TextField
            label='Business Name'
            value={business?.name ?? ''}
            fullWidth
            required
            InputProps={{ readOnly: true }}
            size='medium'
          />

          <Select
            name='tags'
            control={control}
            label='Tags'
            options={tagOptions}
            multiple
            sortOrder='none'
            visibleCount={99999}
            isLoading={contextsLoading}
            fullWidth
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 360 }
              }
            }}
          />
        </Box>
      </Box>

      <Box px={2.5} py={2} borderTop='1px solid #ECECEC' mt='auto'>
        <Stack direction='row' justifyContent='flex-end' spacing={1.5}>
          <Button variant='outlined' onClick={onClose} disabled={isPending} sx={{ borderRadius: '18px' }}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='add-business-tags-form'
            variant='contained'
            disabled={isPending}
            sx={{ borderRadius: '18px' }}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
